import { ref } from 'vue'
import { defineStore } from 'pinia'
import { clientApi } from '../api/client.api'
import { useQueryStore } from '@/shared/stores/query-param.store'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import { CLIENT_SETTINGS } from '../constants/client.constants'
import {
  DASHBOARD_CLIENT_SETTINGS,
  MOTION_CLIENT_SETTINGS,
  ViewType,
  type ClientSetting,
} from '../types/client.enums'
import type { RouteRecordNameGeneric } from 'vue-router'
import type {
  ClientAction,
  ClientCarrier,
  ClientCountry,
  ClientDimension,
  ClientServiceGroup,
  ClientView,
  ClientZipZone,
} from '../models/client.models'
import { ActionType } from '@/modules/action/types/action.enums'

export const useClientStore = defineStore('client', () => {
  const queryStore = useQueryStore()
  const activeViewId = ref<ViewType>()
  const defaultViewId = ref<ViewType>()
  const views = ref<ClientView[]>([])
  const actions = ref<ClientAction[]>([])
  const actionGroups = ref<ClientAction[]>([])
  const carriers = ref<ClientCarrier[]>([])
  const countries = ref<ClientCountry[]>([])
  const serviceGroups = ref<ClientServiceGroup[]>([])
  const zipZones = ref<ClientZipZone[]>([])
  const settings = ref<{ [id: number]: string }>([])
  const dimensions = ref<Record<number, ClientDimension | undefined>>({
    1: undefined,
    2: undefined,
    3: undefined,
  })

  const getSettingRequestByRoute = (routeName: RouteRecordNameGeneric): number[] | null => {
    switch (routeName) {
      case ROUTE_NAMES.MOTION_VIEW:
        return MOTION_CLIENT_SETTINGS
      case ROUTE_NAMES.DASHBOARD:
        return DASHBOARD_CLIENT_SETTINGS
      default:
        return null
    }
  }

  const loadClientSettings = async (routeName: RouteRecordNameGeneric): Promise<boolean> => {
    const request = getSettingRequestByRoute(routeName)
    if (request) {
      const { data } = await clientApi.getClientSettings(request)
      if (data) {
        settings.value = data
        return true
      }
    }

    return false
  }

  const loadClientViews = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientViews()
    if (data) {
      views.value = data.views
      defaultViewId.value = data.activeViewId
      const id = queryStore.getViewId(data.views)
      activeViewId.value = id ? id : data.activeViewId

      return true
    }

    return false
  }

  const loadClientActions = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientActions()
    if (data) {
      actions.value = data
      actionGroups.value = data.filter((action) => action.typeId === ActionType.ActionGroup)

      return true
    }

    return false
  }

  const loadClientCarriers = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientCarriers()
    if (data) {
      carriers.value = data
      return true
    }

    return false
  }

  const loadClientCountries = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientCountries()
    if (data) {
      countries.value = data
      return true
    }

    return false
  }

  const loadClientDimensions = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientDimensions()
    if (data) {
      dimensions.value = data
      return true
    }

    return false
  }

  const loadClientServiceGroups = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientServiceGroups()
    if (data) {
      serviceGroups.value = data
      return true
    }

    return false
  }

  const loadClientZipZones = async (): Promise<boolean> => {
    const { data } = await clientApi.getClientZipZones()
    if (data) {
      zipZones.value = data
      return true
    }

    return false
  }

  const isSettingEnabled = (clientSetting: ClientSetting): boolean => {
    if (activeViewId.value) {
      if (!activeViewId.value) {
        return false
      }

      const viewSettings = CLIENT_SETTINGS[activeViewId.value]
      if (!viewSettings) {
        return false
      }

      for (const [key, value] of Object.entries(viewSettings)) {
        if (value === clientSetting) {
          return settings.value[Number(key)] === '1'
        }
      }
    }

    return true
  }

  return {
    activeViewId,
    defaultViewId,
    views,
    actions,
    actionGroups,
    carriers,
    countries,
    dimensions,
    serviceGroups,
    zipZones,
    loadClientViews,
    loadClientActions,
    loadClientCarriers,
    loadClientCountries,
    loadClientDimensions,
    loadClientServiceGroups,
    loadClientZipZones,
    loadClientSettings,
    isSettingEnabled,
  }
})
